import { EditIcon } from '#assets/ReactSvg.ts';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import { IconLink } from '#components/atoms/link/icon-link/IconLink.tsx';
import { ConfigOverrideValue } from '#components/patterns/config-overrides/ConfigOverrideValue.tsx';
import { CONFIG_OVERRIDE_LABELS } from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { DetailList } from '#components/patterns/detail-list/DetailList.tsx';
import { DetailListRow } from '#components/patterns/detail-list/DetailListRow.tsx';
import type { Host } from '#dn-types/host.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useOverrideMaps } from '../../hooks/useOverrideMaps.ts';
import { makeConfigHalves } from './makeConfigHalves.ts';
import { makeStatsHalves } from './makeStatsHalves.ts';
import { StatsDisclosure } from './StatsDisclosure.tsx';
import styles from './AdvancedConfigCard.module.css';

type Props = {
  host: Pick<Host, 'configOverrides' | 'tags'>;
  className?: string;
};

export function AdvancedConfigCard({ host, className }: Props) {
  const location = useAppLocation();
  const { configMap, statsMap } = useOverrideMaps({
    hostConfigOverrides: host.configOverrides,
    tagNames: host.tags,
  });
  const configs = makeConfigHalves(configMap);
  const stats = makeStatsHalves(statsMap);
  const statsType = statsMap.get('stats.type');

  // Hide card if we have no config overrides for the host
  if (configs.firstHalf.length === 0 && stats.firstHalf.length === 0) {
    return null;
  }

  return (
    <DetailCard
      className={className}
      hoverButton={
        <IconLink to="edit?tab=advanced" state={{ from: location }} title="Edit advanced config">
          <EditIcon />
        </IconLink>
      }
    >
      <h2 className="h4 mb-16">Advanced config</h2>

      <div className={styles.AdvancedConfigCard_tables}>
        <DetailList>
          {configs.firstHalf.map(({ value: configOverride, source }) => (
            <DetailListRow
              key={configOverride.key}
              label={CONFIG_OVERRIDE_LABELS[configOverride.key]}
              value={<ConfigOverrideValue configOverride={configOverride} source={source} />}
            />
          ))}
        </DetailList>
        <DetailList>
          {configs.secondHalf.map(({ value: configOverride, source }) => (
            <DetailListRow
              key={configOverride.key}
              label={CONFIG_OVERRIDE_LABELS[configOverride.key]}
              value={<ConfigOverrideValue configOverride={configOverride} source={source} />}
            />
          ))}
        </DetailList>
      </div>
      <StatsDisclosure type={statsType} firstHalf={stats.firstHalf} secondHalf={stats.secondHalf} />
    </DetailCard>
  );
}
